// Search bar: predictive search autocomplete + collection live filter
(function () {
  const DEBOUNCE_DELAY = 250;
  const DESKTOP_PRODUCT_LIMIT = 12;
  const DESKTOP_PRODUCT_COLUMNS = 6;

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  // ── Predictive Search Autocomplete ──────────────────────────────────────────

  class SearchAutocomplete {
    constructor(input, dropdown) {
      this.input = input;
      this.dropdown = dropdown;
      this.abortController = null;
      this.menuContainer = input.closest('[data-desktop-menu-container]');

      this.onInput = debounce(this._onInput.bind(this), DEBOUNCE_DELAY);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this._onFocus.bind(this));
      this.form = this.input.closest('[data-search-form]');
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          if (!this.input.value.trim()) {
            e.preventDefault();
            window.location.href = '/collections/all';
          }
        });
      }
      document.addEventListener('click', this._onDocumentClick.bind(this));
      this.input.addEventListener('keydown', this._onKeydown.bind(this));
    }

    _hideMenuLinks() {
      if (this.menuContainer) {
        this.menuContainer.querySelectorAll('.desktop-menu-content > ul').forEach(el => {
          el.style.visibility = 'hidden';
        });
      }
      const mobileMenuEl = this.input.closest('#mobile-menu');
      if (mobileMenuEl) {
        mobileMenuEl.querySelectorAll('.mobile-accordion-list, .mobile-menu-picker, .mobile-menu-image').forEach(el => {
          el.style.visibility = 'hidden';
        });
      }
    }

    _showMenuLinks() {
      if (this.menuContainer) {
        this.menuContainer.querySelectorAll('.desktop-menu-content > ul').forEach(el => {
          el.style.visibility = '';
        });
      }
      const mobileMenuEl = this.input.closest('#mobile-menu');
      if (mobileMenuEl) {
        mobileMenuEl.querySelectorAll('.mobile-accordion-list, .mobile-menu-picker, .mobile-menu-image').forEach(el => {
          el.style.visibility = '';
        });
      }
    }

    _onFocus() {
      this._hideMenuLinks();
      const mobileMenuEl = this.input.closest('#mobile-menu');
      if (mobileMenuEl) {
        this._blockScroll = (e) => {
          if (!e.target.closest('[data-search-autocomplete]')) e.preventDefault();
        };
        mobileMenuEl.addEventListener('touchmove', this._blockScroll, { passive: false });
        this.input.addEventListener('blur', () => {
          mobileMenuEl.removeEventListener('touchmove', this._blockScroll);
        }, { once: true });
      }
    }

    async _onInput() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this._hide();
        if (q.length === 0) this._showMenuLinks();
        return;
      }
      this._hideMenuLinks();
      this._fetch(q);
    }

    async _fetch(q) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const isDesktop = this.input.dataset.context === 'desktop';

      try {
        const url = isDesktop
          ? '/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product,collection&resources[limit]=' + DESKTOP_PRODUCT_LIMIT + '&resources[limit_scope]=each&resources[options][unavailable_products]=last'
          : '/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=30&resources[options][unavailable_products]=last';
        const res = await fetch(url, { signal: this.abortController.signal });
        if (!res.ok) return;
        const data = await res.json();

        if (isDesktop) {
          const results = data?.resources?.results;
          this._renderGrid(results?.products || [], results?.collections || []);
        } else {
          const products = data?.resources?.results?.products || [];
          this._render(products);
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.warn('Search autocomplete error:', e);
      }
    }

    _positionDropdown() {
      const inputRect = this.input.getBoundingClientRect();
      const formEl = this.input.closest('[data-search-form]');
      const anchorRect = formEl ? formEl.getBoundingClientRect() : inputRect;
      const mobileMenuEl = this.input.closest('#mobile-menu');
      const gap = 16;
      const itemHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--font-size-big') || '18') * 1.2 + 12.8;
      const maxByItems = itemHeight * 5;

      if (mobileMenuEl) {
        // Mobile: move dropdown into #mobile-menu and use absolute positioning
        // so it scrolls with the menu and isn't clipped by viewport/keyboard issues
        if (this.dropdown.parentElement !== mobileMenuEl) {
          mobileMenuEl.appendChild(this.dropdown);
        }
        const menuRect = mobileMenuEl.getBoundingClientRect();
        const topRelToMenu = inputRect.bottom - menuRect.top + gap;
        const availableDown = menuRect.height - topRelToMenu - gap;
        this.dropdown.style.position = 'absolute';
        this.dropdown.style.top = topRelToMenu + 'px';
        this.dropdown.style.bottom = '';
        this.dropdown.style.left = (anchorRect.left - menuRect.left) + 'px';
        this.dropdown.style.width = anchorRect.width + 'px';
        this.dropdown.style.maxHeight = Math.min(maxByItems, availableDown) + 'px';
      } else {
        // Desktop: inline mega-search grid below the input, panel grows to fit —
        // no fixed positioning/height cap (styling handled by .search-autocomplete--desktop)
      }
    }

    _renderGrid(products, collections) {
      if (!products.length && !collections.length) {
        this._hide();
        return;
      }

      const collectionsHtml = collections.map(c =>
        `<li><a class="text-small hover:opacity-50" href="${c.url}">${c.title}</a></li>`
      ).join('');

      const productsHtml = products.map(p => {
        const image = p.featured_image?.url || p.image || '';
        return `<li>
          <a href="${p.url}" class="block aspect-square bg-gray-50">
            ${image ? `<img src="${image}" alt="${p.title}" class="w-full h-full object-contain" loading="lazy">` : ''}
          </a>
        </li>`;
      }).join('');

      const hasCollections = collections.length > 0;
      const outerColumns = hasCollections ? '1fr 3fr' : '1fr';

      this.dropdown.innerHTML = `<div class="grid gap-8" style="grid-template-columns: ${outerColumns};">
        ${hasCollections ? `<div>
          <div class="text-small font-medium mb-2">collections</div>
          <ul class="space-y-1">${collectionsHtml}</ul>
        </div>` : ''}
        ${productsHtml ? `<ul class="grid gap-4" style="grid-template-columns: repeat(${DESKTOP_PRODUCT_COLUMNS}, minmax(0, 1fr));">${productsHtml}</ul>` : ''}
      </div>`;
      this.dropdown.classList.add('is-visible');
    }

    _render(products) {
      if (!products.length) {
        this._hide();
        return;
      }

      this._positionDropdown();

      function getDisplayTitle(title) {
        const words = title.trim().split(/\s+/);
        // If title contains "cap" as a word, cut off everything after it
        const capIndex = words.findIndex(w => w.toLowerCase() === 'cap');
        if (capIndex !== -1 && capIndex < words.length - 1) {
          return words.slice(0, capIndex + 1).join(' ');
        }
        return title;
      }

      const seen = new Set();
      const unique = products.filter(p => {
        const key = getDisplayTitle(p.title).toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      this.dropdown.innerHTML = unique.map(p => {
        const displayTitle = getDisplayTitle(p.title);
        const href = '/search?type=product&q=' + encodeURIComponent(displayTitle);
        return `<a class="search-autocomplete__item" href="${href}" data-autocomplete-item>
          <span class="search-autocomplete__title">${displayTitle}</span>
        </a>`;
      }).join('');

      this.dropdown.classList.add('is-visible');
    }

    _hide() {
      this.dropdown.classList.remove('is-visible');
      this.dropdown.innerHTML = '';
      this._showMenuLinks();
    }

    _onDocumentClick(e) {
      if (!this.input.contains(e.target) && !this.dropdown.contains(e.target)) {
        this._hide();
        if (!this.form?.dataset.submitting) {
          this.input.value = '';
        }
        this._showMenuLinks();
      }
    }

    _onKeydown(e) {
      if (e.key === 'Enter') {
        if (this.form) this.form.dataset.submitting = 'true';
      }
      if (e.key === 'Escape') {
        this._hide();
        this.input.blur();
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.dropdown.querySelector('[data-autocomplete-item]')?.focus();
      }
    }
  }

  // ── Collection Live Filter ────────────────────────────────────────────────────

  class CollectionSearchFilter {
    constructor(input) {
      this.input = input;
      this.grid = document.getElementById('product-grid');
      if (!this.grid) return;
      this.abortController = null;

      // On search results page: pre-fill input from URL and clear on first focus
      if (window.location.pathname === '/search') {
        const urlQ = new URLSearchParams(window.location.search).get('q');
        if (urlQ) {
          this.input.value = urlQ;
          this.input.addEventListener('focus', () => { this.input.value = ''; }, { once: true });
        }
      }

      this.input.addEventListener('input', debounce(this._onInput.bind(this), 200));

      // Always prevent form submit — filter is always local, no page navigation
      const form = this.input.closest('form');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this._onInput();
        });
      }
    }

    _showAll() {
      this.grid.querySelectorAll('.grid__item').forEach(item => { item.style.display = ''; });
      const noResults = document.getElementById('collection-no-results');
      if (noResults) noResults.style.display = 'none';
    }

    _applyIds(matchedIds) {
      const noResults = document.getElementById('collection-no-results');
      let visibleCount = 0;
      this.grid.querySelectorAll('.grid__item').forEach(item => {
        const id = item.querySelector('[data-product-id]')?.dataset.productId;
        const matches = id && matchedIds.has(id);
        item.style.display = matches ? '' : 'none';
        if (matches) visibleCount++;
      });
      if (noResults) noResults.style.display = visibleCount === 0 ? 'block' : 'none';
    }

    async _onInput() {
      const q = this.input.value.trim();
      if (!q) {
        this._showAll();
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      try {
        const url = '/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=100&resources[options][unavailable_products]=last';
        const res = await fetch(url, { signal: this.abortController.signal });
        if (!res.ok) return;
        const data = await res.json();
        const products = data?.resources?.results?.products || [];
        const matchedIds = new Set(products.map(p => String(p.id)));
        this._applyIds(matchedIds);
      } catch (e) {
        if (e.name !== 'AbortError') console.warn('Collection filter error:', e);
      }
    }
  }

  // ── Init ─────────────────────────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('[data-search-input]').forEach(input => {
      const form = input.closest('[data-search-form]');
      if (!form) return;

      const isCollection = input.dataset.context === 'collection';

      if (!isCollection) {
        const dropdown = form.querySelector('[data-search-autocomplete]');
        if (dropdown) new SearchAutocomplete(input, dropdown);
      } else {
        new CollectionSearchFilter(input);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
